import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import type { HousingFilters, Listing, PropertyKind, SearchIntent } from '../types/housing';
import { listings } from '../data/housing';
import { DEFAULT_FILTERS, applyHousingSearch } from '../utils/housing';

/**
 * ONE housing state, TWO presentations.
 *
 * Both Find-a-home variants read and write this context. Nothing about the
 * search — query, campus, budget, move-in, category, filters, sort, saved
 * homes, the selected property, the map viewport, the list/map preference —
 * belongs to a variant. That is the whole point: when the presentation
 * changes, the person's work must survive untouched.
 */

export type HousingView = 'list' | 'split' | 'map';
export type HousingCategory = 'All' | PropertyKind;
export const HOUSING_CATEGORIES: readonly HousingCategory[] = ['All', 'Hostel', 'Student residence', 'Apartment', 'Shared apartment', 'Self-contained', 'Co-living'];
interface HousingState {
  /* search */
  query: string;
  setQuery: (value: string) => void;
  intent: SearchIntent;
  setIntent: (intent: SearchIntent) => void;
  category: HousingCategory;
  setCategory: (category: HousingCategory) => void;
  filters: HousingFilters;
  setFilters: (filters: HousingFilters) => void;
  clearAll: () => void;
  activeChips: {
    id: string;
    label: string;
    clear: () => void;
  }[];

  /* results */
  results: Listing[];

  /* selection + spatial */
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  hoverId: string | null;
  setHoverId: (id: string | null) => void;
  mapCentre: {
    lat: number;
    lng: number;
    zoom: number;
  } | null;
  setMapCentre: (centre: {
    lat: number;
    lng: number;
    zoom: number;
  }) => void;
  searchAsMoved: boolean;
  setSearchAsMoved: (value: boolean) => void;

  /* presentation preference — a person's choice, not a variant's */
  view: HousingView;
  setView: (view: HousingView) => void;

  /* shortlist */
  saved: string[];
  isSaved: (id: string) => boolean;
  toggleSaved: (id: string) => void;
}
const DEFAULT_INTENT: SearchIntent = {
  campusId: 'campus-akoka',
  budgetMax: 750000,
  moveIn: '2026-09-01',
  kinds: []
};
const HousingContext = createContext<HousingState | null>(null);
export function HousingProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [query, setQuery] = useState('');
  const [intent, setIntent] = useState<SearchIntent>(DEFAULT_INTENT);
  const [category, setCategory] = useState<HousingCategory>('All');
  const [filters, setFilters] = useState<HousingFilters>(DEFAULT_FILTERS);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [mapCentre, setMapCentre] = useState<{
    lat: number;
    lng: number;
    zoom: number;
  } | null>(null);
  const [searchAsMoved, setSearchAsMoved] = useState(true);
  const [view, setView] = useState<HousingView>('split');
  const [saved, setSaved] = useState<string[]>([]);
  const toggleSaved = useCallback((id: string) => {
    setSaved(current => current.includes(id) ? current.filter(entry => entry !== id) : [...current, id]);
  }, []);
  const clearAll = useCallback(() => {
    setQuery('');
    setCategory('All');
    setFilters(DEFAULT_FILTERS);
    setIntent(DEFAULT_INTENT);
  }, []);
  const results = useMemo(() => {
    /* The category tab is a view onto the same intent, so it narrows kinds
       rather than living as a parallel filter the other variant can't see. */
    const scopedIntent: SearchIntent = category === 'All' ? intent : {
      ...intent,
      kinds: [category as PropertyKind]
    };
    const matched = applyHousingSearch(listings, scopedIntent, filters);
    const text = query.trim().toLowerCase();
    if (!text) return matched;
    return matched.filter(listing => [listing.name, listing.neighbourhood, listing.address, listing.kind, listing.roomType].join(' ').toLowerCase().includes(text));
  }, [category, intent, filters, query]);
  const activeChips = useMemo(() => {
    const chips: {
      id: string;
      label: string;
      clear: () => void;
    }[] = [];
    if (query.trim()) chips.push({
      id: 'query',
      label: `“${query.trim()}”`,
      clear: () => setQuery('')
    });
    if (category !== 'All') chips.push({
      id: 'category',
      label: category,
      clear: () => setCategory('All')
    });
    chips.push({
      id: 'budget',
      label: `Up to ₦${intent.budgetMax.toLocaleString()}`,
      clear: () => setIntent({
        ...intent,
        budgetMax: 2000000
      })
    });
    if (filters.verifiedOnly) chips.push({
      id: 'verified',
      label: 'Inspected only',
      clear: () => setFilters({
        ...filters,
        verifiedOnly: false
      })
    });
    if (filters.maxMinutes < 60) chips.push({
      id: 'minutes',
      label: `Within ${filters.maxMinutes} min`,
      clear: () => setFilters({
        ...filters,
        maxMinutes: 60
      })
    });
    filters.facilities.forEach(facility => chips.push({
      id: `facility-${facility}`,
      label: facility,
      clear: () => setFilters({
        ...filters,
        facilities: filters.facilities.filter(entry => entry !== facility)
      })
    }));
    return chips;
  }, [query, category, intent, filters]);
  const value: HousingState = {
    query,
    setQuery,
    intent,
    setIntent,
    category,
    setCategory,
    filters,
    setFilters,
    clearAll,
    activeChips,
    results,
    activeId,
    setActiveId,
    hoverId,
    setHoverId,
    mapCentre,
    setMapCentre,
    searchAsMoved,
    setSearchAsMoved,
    view,
    setView,
    saved,
    isSaved: (id: string) => saved.includes(id),
    toggleSaved
  };
  return <HousingContext.Provider value={value} data-id="element-6058" data-mp="20srlkqcz7951">{children}</HousingContext.Provider>;
}
export function useHousing(): HousingState {
  const context = useContext(HousingContext);
  if (!context) throw new Error('useHousing must be used inside HousingProvider');
  return context;
}